import { Node, Edge } from 'reactflow';
import { ProcessorNodeData, SubAgentConfig } from '../types/pipeline';

/**
 * Sub-Agent Connection Intelligence (SWRM-121 Phase 3)
 * 
 * Utilities for detecting and managing sub-agent membership based on
 * connections to orchestrator bottom handles.
 */

/**
 * Detect if a connection is to an orchestrator's sub-agent handle
 */
export const isSubAgentConnection = (connection: {
  source?: string | null;
  sourceHandle?: string | null;
  target?: string | null;
  targetHandle?: string | null;
}): boolean => {
  return connection.sourceHandle === 'sub_agent_output';
};

/**
 * Find all nodes in a sub-agent chain starting from a given node.
 * Follows next_node execution flow edges to collect the entire chain.
 * 
 * @param startNodeId - The node to start from
 * @param subAgentName - The sub-agent name to filter by (only nodes with this name)
 * @param nodes - All nodes in the graph
 * @param edges - All edges in the graph
 * @returns Array of node IDs in the chain
 */
export const findSubAgentChain = (
  startNodeId: string,
  subAgentName: string | undefined,
  nodes: Node<ProcessorNodeData>[],
  edges: Edge[]
): string[] => {
  if (!subAgentName) return [startNodeId];
  
  const chain: string[] = [];
  const visited = new Set<string>();
  let currentNodeId: string | null = startNodeId;
  
  /* eslint-disable no-loop-func */
  while (currentNodeId && !visited.has(currentNodeId)) {
    visited.add(currentNodeId);
    
    const node = nodes.find((n) => n.id === currentNodeId);
    if (!node || node.data.subAgentName !== subAgentName) {
      break;
    }
    
    chain.push(currentNodeId);
    
    // Find next node in execution flow
    const nextEdge = edges.find(
      (e) => e.source === currentNodeId && e.targetHandle === 'previous_node'
    );
    
    currentNodeId = nextEdge?.target || null;
  }
  /* eslint-enable no-loop-func */
  
  return chain;
};

/**
 * Find the orchestrator node that a processor belongs to (if any)
 * by tracing back through execution flow edges
 */
export const findOrchestratorForNode = (
  nodeId: string,
  nodes: Node<ProcessorNodeData>[],
  edges: Edge[]
): { orchestratorId: string; orchestratorNode: Node<ProcessorNodeData> } | null => {
  // First, check if this node is directly connected to an orchestrator's sub_agent_output
  const directConnection = edges.find(
    (e) => e.target === nodeId && e.sourceHandle === 'sub_agent_output'
  );
  
  if (directConnection) {
    const orchNode = nodes.find((n) => n.id === directConnection.source);
    if (orchNode && orchNode.type === 'orchestrator') {
      return { 
        orchestratorId: orchNode.id, 
        orchestratorNode: orchNode as Node<ProcessorNodeData> 
      };
    }
  }
  
  // If not directly connected, trace back through execution flow
  // to see if we're part of a sub-agent chain
  let currentNodeId = nodeId;
  const visited = new Set<string>();
  
  /* eslint-disable no-loop-func */
  while (currentNodeId && !visited.has(currentNodeId)) {
    visited.add(currentNodeId);
    
    // Find incoming execution edge
    const incomingExecution = edges.find(
      (e) => e.target === currentNodeId && e.sourceHandle === 'next_node'
    );
    
    if (!incomingExecution) {
      // Check if incoming edge is from sub_agent_output
      const subAgentEdge = edges.find(
        (e) => e.target === currentNodeId && e.sourceHandle === 'sub_agent_output'
      );
      
      if (subAgentEdge) {
        const orchNode = nodes.find((n) => n.id === subAgentEdge.source);
        if (orchNode && orchNode.type === 'orchestrator') {
          return { 
            orchestratorId: orchNode.id, 
            orchestratorNode: orchNode as Node<ProcessorNodeData> 
          };
        }
      }
      
      break; // No more incoming edges
    }
    
    currentNodeId = incomingExecution.source;
  }
  /* eslint-enable no-loop-func */
  
  return null;
};

/**
 * Find all nodes that belong to a specific sub-agent chain
 */
export const findNodesInSubAgent = (
  orchestratorId: string,
  subAgentName: string,
  nodes: Node<ProcessorNodeData>[]
): Node<ProcessorNodeData>[] => {
  return nodes.filter(
    (node) => 
      node.data.orchestratorNodeId === orchestratorId &&
      node.data.subAgentName === subAgentName
  );
};

/**
 * Build the execution chain for a sub-agent by following next_node connections
 */
export const buildSubAgentChain = (
  firstNodeId: string,
  nodes: Node<ProcessorNodeData>[],
  edges: Edge[]
): Node<ProcessorNodeData>[] => {
  const chain: Node<ProcessorNodeData>[] = [];
  let currentNodeId: string | null = firstNodeId;
  const visited = new Set<string>();
  
  /* eslint-disable no-loop-func */
  while (currentNodeId && !visited.has(currentNodeId)) {
    visited.add(currentNodeId);
    
    const node = nodes.find((n) => n.id === currentNodeId);
    if (!node) break;
    
    chain.push(node);
    
    // Find next node via next_node handle
    const nextEdge = edges.find(
      (e) => e.source === currentNodeId && e.sourceHandle === 'next_node'
    );
    
    if (!nextEdge) break;
    
    // Check if next node is still in the same sub-agent
    const nextNode = nodes.find((n) => n.id === nextEdge.target);
    if (!nextNode) break;
    
    // If next node belongs to different orchestrator or is in main pipeline, stop
    if (
      nextNode.data.orchestratorNodeId !== node.data.orchestratorNodeId ||
      nextNode.data.subAgentName !== node.data.subAgentName
    ) {
      break;
    }
    
    currentNodeId = nextEdge.target;
  }
  /* eslint-enable no-loop-func */
  
  return chain;
};

/**
 * Get all sub-agent names for an orchestrator
 */
export const getSubAgentNames = (
  orchestratorNode: Node<ProcessorNodeData>
): string[] => {
  const subAgents = (orchestratorNode.data.config?.sub_agents || []) as SubAgentConfig[];
  return subAgents.map((sa) => sa.name);
};

/**
 * Check if a sub-agent name already exists for an orchestrator
 */
export const isSubAgentNameTaken = (
  orchestratorNode: Node<ProcessorNodeData>,
  name: string
): boolean => {
  const existingNames = getSubAgentNames(orchestratorNode);
  return existingNames.includes(name);
};

/**
 * Validate snake_case format
 */
export const isValidSnakeCase = (name: string): boolean => {
  const snakeCasePattern = /^[a-z][a-z0-9_]*$/;
  return snakeCasePattern.test(name);
};

/**
 * Generate a unique sub-agent name if the provided name is taken
 */
export const generateUniqueSubAgentName = (
  orchestratorNode: Node<ProcessorNodeData>,
  baseName: string
): string => {
  let name = baseName;
  let counter = 1;
  
  while (isSubAgentNameTaken(orchestratorNode, name)) {
    name = `${baseName}_${counter}`;
    counter++;
  }
  
  return name;
};

/**
 * Detect all sub-agent memberships and return updated nodes
 * This is called after edge changes to update node memberships
 * 
 * Uses iterative propagation to ensure subAgentName is correctly
 * inherited through entire chains (not just direct connections)
 */
export const detectAndUpdateSubAgentMembership = (
  nodes: Node<ProcessorNodeData>[],
  edges: Edge[]
): Node<ProcessorNodeData>[] => {
  let currentNodes = nodes;
  let hasChanges = true;
  let iterations = 0;
  const MAX_ITERATIONS = 10; // Prevent infinite loops
  
  // Iterate until no changes occur (convergence) or max iterations reached
  /* eslint-disable no-loop-func */
  while (hasChanges && iterations < MAX_ITERATIONS) {
    hasChanges = false;
    iterations++;
    
    currentNodes = currentNodes.map((node) => {
      // Skip orchestrator nodes and system input
      if (node.type === 'orchestrator' || node.type === 'systemInput') {
        return node;
      }
      
      // Find which orchestrator this node belongs to (if any)
      const orchestratorInfo = findOrchestratorForNode(node.id, currentNodes, edges);
      
      if (!orchestratorInfo) {
        // Node is in main pipeline - clear any orchestrator references
        if (node.data.orchestratorNodeId || node.data.subAgentName) {
          hasChanges = true;
          return {
            ...node,
            data: {
              ...node.data,
              orchestratorNodeId: undefined,
              subAgentName: undefined,
            },
          };
        }
        return node;
      }
      
      // Node belongs to an orchestrator - we need to determine which sub-agent
      // If node doesn't have subAgentName, try to inherit from incoming node
      if (!node.data.subAgentName) {
        // Find incoming execution edge
        const incomingEdge = edges.find(
          (e) => e.target === node.id && e.sourceHandle === 'next_node'
        );
        
        if (incomingEdge) {
          // Check if source node has a sub-agent name (look in CURRENT nodes)
          const sourceNode = currentNodes.find((n) => n.id === incomingEdge.source);
          
          if (sourceNode?.data.subAgentName && 
              sourceNode.data.orchestratorNodeId === orchestratorInfo.orchestratorId) {
            // Inherit sub-agent name from source
            hasChanges = true;
            return {
              ...node,
              data: {
                ...node.data,
                orchestratorNodeId: orchestratorInfo.orchestratorId,
                subAgentName: sourceNode.data.subAgentName,
              },
            };
          }
        }
        
        // No subAgentName to inherit - just set orchestratorNodeId if not set
        if (node.data.orchestratorNodeId !== orchestratorInfo.orchestratorId) {
          hasChanges = true;
          return {
            ...node,
            data: {
              ...node.data,
              orchestratorNodeId: orchestratorInfo.orchestratorId,
            },
          };
        }
        
        return node;
      }
      
      // Update orchestrator reference if changed
      if (node.data.orchestratorNodeId !== orchestratorInfo.orchestratorId) {
        hasChanges = true;
        return {
          ...node,
          data: {
            ...node.data,
            orchestratorNodeId: orchestratorInfo.orchestratorId,
          },
        };
      }
      
      return node;
    });
  }
  /* eslint-enable no-loop-func */
  
  return currentNodes;
};

/**
 * Remove node from sub-agent (called when disconnecting)
 */
export const removeNodeFromSubAgent = (
  node: Node<ProcessorNodeData>
): Node<ProcessorNodeData> => {
  return {
    ...node,
    data: {
      ...node.data,
      orchestratorNodeId: undefined,
      subAgentName: undefined,
    },
  };
};

